import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState, ChartControls } from '../ui';
import { useCustomerTrends, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface CustomerTrendsChartProps extends SectionProps {
  branchId: number;
  months?: number;
}

const CustomerTrendsChart: React.FC<CustomerTrendsChartProps> = ({
  branchId,
  months = 12,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: trendsData, isLoading, isError, error, refetch } = useCustomerTrends(branchId, months);

  // Component metadata
  const componentId = 'customer-trends-chart';
  const componentTitle = 'تعداد مشتریان جدید، قدیمی و غیر فعال در یک سال گذشته ';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری روند…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage 
          message="خطا در دریافت روند مشتریان"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!trendsData || trendsData.length === 0) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  const chartData = {
    labels: trendsData.map(item => item.month),
    datasets: [
      {
        label: 'مشتریان قدیمی',
        data: trendsData.map(item => item.oldCustomers),
        backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[0],
        borderWidth: 2,
        borderRadius: 6
      },
      {
        label: 'مشتریان جدید',
        data: trendsData.map(item => item.newCustomers),
        backgroundColor: getChartBackgroundColor(1, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[1],
        borderWidth: 2,
        borderRadius: 6
      },
      {
        label: 'غیر فعال در یک سال گذشته ',
        data: trendsData.map(item => item.customerLostThismonth),
        backgroundColor: getChartBackgroundColor(2, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[2],
        borderWidth: 2,
        borderRadius: 6
      }
    ]
  };

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="customerAnalytics"
          onInfo={() => openGuide(CHART_GUIDES.customerTrendsChart)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'customerAnalytics',
            componentType: 'chart',
            description: 'تحلیل روند تعداد مشتریان جدید، قدیمی و غیر فعال در یک سال گذشته  در طول زمان',
            props: { branchId, months, compact },
            gridLayout: {
              containerClass: 'col-span-1'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
        />
      </div>
      <h3 className="title-card">
        تعداد مشتریان جدید، قدیمی و غیر فعال در یک سال گذشته 
      </h3>
      <div style={{ height: 350, width: '100%', padding: '20px 0' }}>
        <Bar
          data={chartData}
          options={{
            ...getChartJsOptions(currentTheme),
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = formatNumber(context.parsed.y);
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  }
                }
              }
            },
            scales:{
                    ...getChartJsOptions(currentTheme).scales,
                    x:{
                      ...getChartJsOptions(currentTheme).scales.x,
                      ticks: {
                      ...getChartJsOptions(currentTheme).scales?.x?.ticks,
                      autoSkip: false,
                      maxRotation: 45,
                      minRotation: 45,
                    },
                    },
                    y:{
                      ...getChartJsOptions(currentTheme).scales.y,
                    }
                  }
          }}
        />
      </div>
    </div>
  );
};

export default CustomerTrendsChart;